import { computeCompoundInterest, type CompoundInterestYearPoint } from "./compound-interest";

export interface FireInput {
  currentSavings: number;
  monthlyContribution: number;
  /** Angenommene, konstante jährliche Rendite als Dezimalzahl (0,07 = 7 %). */
  annualReturnRate: number;
  /** Angenommene jährliche Ausgaben im Ruhestand. */
  annualExpenses: number;
  /** Entnahmerate als Dezimalzahl (0,04 = 4 %-Regel). */
  withdrawalRate: number;
}

export interface FireResult {
  /** Zielkapital = jährliche Ausgaben / Entnahmerate. */
  fireNumber: number;
  /** Erstes Jahr, in dem die Projektion das Zielkapital erreicht - undefined, wenn nicht innerhalb von 60 Jahren. */
  yearsToFire: number | undefined;
  points: CompoundInterestYearPoint[];
}

/**
 * FIRE-Rechner (backlog.md -> Aktienfinder-Benchmark, "FIRE-/Finanzielle-Freiheit-Rechner
 * ... passt zum geplanten Dividenden-/Sparquoten-Content") - rein hypothetische
 * Modellrechnung, keine Anlageberatung. Nutzt die bereits getestete
 * computeCompoundInterest()-Wachstumslogik weiter (kein doppelter Zinseszins-Code) und
 * sucht darin nur das erste Jahr, in dem der angesparte Betrag das klassische
 * "25x Jahresausgaben"-Zielkapital (Kehrwert der Entnahmerate, Standard-4%-Regel-Formel)
 * erreicht. Reine Funktion, kein I/O.
 */
export function computeFireProjection(input: FireInput): FireResult | undefined {
  const { currentSavings, monthlyContribution, annualReturnRate, annualExpenses, withdrawalRate } = input;

  if (
    !Number.isFinite(annualExpenses) ||
    annualExpenses <= 0 ||
    !Number.isFinite(withdrawalRate) ||
    withdrawalRate <= 0 ||
    withdrawalRate > 0.2
  ) {
    return undefined;
  }

  const projection = computeCompoundInterest({
    initialCapital: currentSavings,
    monthlyContribution,
    annualReturnRate,
    years: 60,
  });
  if (!projection) return undefined;

  const fireNumber = annualExpenses / withdrawalRate;
  const crossing = projection.points.find((p) => p.balance >= fireNumber);

  return { fireNumber, yearsToFire: crossing?.year, points: projection.points };
}
